// src/components/plan/planCopy.js
// Pooled inbox SP5 — every web string the plan screens, the gate notices,
// Profile's new lines, admin Packages' new fields and the inbox's plan tag
// show (spec § Screens per app → Web; prototype proto/plan/*.html). Screens
// and tests import keys; only tests/components/plan-copy.test.js repeats the
// literals. Apostrophes are curly (R206) except where the prototype draws a
// straight one ("What's included:", flagged in spec § Departures). Strings the
// spec marks proposed, and most of the prototype's, carry `// Wording pending`
// for the Astra copy pass.

// ---- Money and dates -----------------------------------------------------------------
// Integer cents from the server → "$29.99", "$0.00", "$49.00".
export const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
// The setup fee as the prototype prints it: "$99", or "$99.50" when it has cents.
export const moneyShort = (cents) =>
  Number(cents) % 100 === 0 ? `$${Number(cents) / 100}` : money(cents);
// An ISO instant → "November 4, 2026" (Profile's first-charge line).
export const longDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

// ---- The frame (all three screens) ------------------------------------------------
export const PLAN_LOGO_ALT = "STARS, powered by Astra";

// ---- Choose your plan (/plan, proto package.html) ---------------------------------
export const PLAN_TITLE = "Choose your plan"; // Wording pending
export const PLAN_SUB =
  "Chat with your advisors is included on every plan. Meetings are what differ."; // Wording pending
export const PLAN_BACK = "Back";
export const PLAN_FREE_TITLE = "Your first month is free on every plan"; // Wording pending
// The fee is the platform amount GET /billing/plans reports, always waived (R226).
export const PLAN_FREE_BODY = (setupFeeCents) =>
  `${setupFeeCents ? `The ${moneyShort(setupFeeCents)} setup fee is waived. ` : ""}Nothing is charged today, and you can cancel any time before your first charge.`; // Wording pending
// R224: a client who already had a free month pays from the first day.
export const PLAN_RETURNING_TITLE = "Welcome back"; // Wording pending (proposed)
export const PLAN_RETURNING_BODY =
  "You’ve had your free month, so your plan is charged from today. You can cancel any time."; // Wording pending (proposed)
export const PER_MONTH = "/ month";
export const GET_STARTED = "Get Started"; // Wording pending
// Straight apostrophe as drawn (spec § Departures; R206 flag for Astra).
export const WHATS_INCLUDED = "What's included:"; // Wording pending
export const YOUR_PLAN_PILL = "Your plan"; // Wording pending
export const CURRENT_PLAN = "Current Plan"; // Wording pending
export const NO_PACKAGES = "No packages available at this time.";
export const PLAN_FOOTNOTE = (addOnCents) =>
  `Need more time with your advisors? Extra meetings are ${money(addOnCents)} each, bought when you book.`; // Wording pending

// ---- Add a card (/plan/payment, proto payment.html) --------------------------------
export const PAY_TITLE = "Add a card for after your free month"; // Wording pending
export const PAY_TITLE_RETURNING = "Add a card for your plan"; // Wording pending (proposed)
export const ORDER_SUMMARY = "Order summary"; // Wording pending
export const LEDGER_PLAN = (name) => `${name} plan, monthly`; // Wording pending
export const LEDGER_FIRST_MONTH = "First month"; // Wording pending
export const LEDGER_FREE = "Free"; // Wording pending
export const LEDGER_SETUP_FEE = "Setup fee"; // Wording pending
export const LEDGER_WAIVED = "Waived"; // Wording pending
export const LEDGER_DUE_TODAY = "Due today"; // Wording pending
export const LEDGER_THEN = "Then, each month after your free month"; // Wording pending
export const LEDGER_THEN_RETURNING = "Then, each month"; // Wording pending (proposed)
export const PAY_FOOT =
  "Your card is saved for after your free month. Cancel any time before then."; // Wording pending
export const PAY_FOOT_RETURNING =
  "Your card is charged today and then each month. Cancel any time."; // Wording pending (proposed)
export const CHANGE_PLAN = "Change plan"; // Wording pending
export const CARD_DETAILS = "Card details"; // Wording pending
export const START_FREE_MONTH = "Start my free month"; // Wording pending
export const START_PLAN = "Start my plan"; // Wording pending (proposed)
export const PLEASE_WAIT = "Please wait..."; // Wording pending
export const PROCESSED_BY_STRIPE = "Payment processed by Stripe"; // Wording pending
// Shown only when Stripe sends no message of its own with a refused card.
export const CARD_DECLINED = "Your card was declined."; // Wording pending
// The server's 402 card_declined may carry Stripe's decline code (R267).
export const CARD_DECLINED_WITH = (declineCode) =>
  declineCode
    ? `Your card was declined (${String(declineCode).replace(/_/g, " ")}).`
    : CARD_DECLINED; // Wording pending
export const CONFLICT_RETRY =
  "Something changed on our side — please try again."; // Wording pending
export const STRIPE_UNREACHABLE =
  "We couldn’t reach Stripe. Check your connection and try again."; // Wording pending (proposed)

// ---- Your free month has started (/plan/started, proto confirm.html) ---------------
export const STARTED_TITLE = "Your free month has started"; // Wording pending
export const STARTED_TITLE_RETURNING = "Your plan has started"; // Wording pending (proposed)
export const STARTED_BODY = (name) =>
  `You’re on ${name}. Nothing was charged today, and your advisors are ready for your first message.`; // Wording pending
export const STARTED_BODY_RETURNING = (name) =>
  `You’re on ${name}. Your advisors are ready for your first message.`; // Wording pending (proposed)
export const LEDGER_PLAN_TITLE = (name) => `${name} plan`; // Wording pending
export const LEDGER_CHARGED_TODAY = "Charged today"; // Wording pending
export const MESSAGE_ADVISORS = "Message your advisors"; // Wording pending
export const GO_TO_DASHBOARD = "Go to my dashboard"; // Wording pending

// ---- The dashboard notice (owner item 20, proposed) --------------------------------
export const NOTICE_NEVER_PAID =
  "Choose a plan to start messaging your advisors. Your first month is free."; // Wording pending (proposed)
export const NOTICE_LAPSED =
  "Your plan has ended. Choose a plan to message your advisors again."; // Wording pending (proposed)
export const NOTICE_PAYMENT_FAILED =
  "Your last payment didn’t go through. Update your card to keep messaging your advisors."; // Wording pending (proposed)
export const NOTICE_CHOOSE_PLAN = "Choose your plan"; // Wording pending
export const NOTICE_UPDATE_CARD = "Update your card"; // Wording pending (proposed)
// Copy exception (fix wave round 2): an App Store client's failed payment.
export const APPLE_PAST_DUE_NOTE =
  "Your last App Store payment didn’t go through. Update it in the App Store on your iPhone to keep messaging your advisors."; // Wording pending (proposed)

// ---- Profile (legacy Subscription panel; proto profile.html) -----------------------
export const FIRST_CHARGE_ON = (iso) => `First charge on ${longDate(iso)}`; // Wording pending (proposed, owner item 18)
export const PLAN_ENDS_ON = (iso) => `Your plan ends on ${longDate(iso)}`; // Wording pending (proposed)
export const MANAGE_IN_APP_STORE = "Manage in the App Store"; // Wording pending (proposed)
export const APPLE_MANAGE_URL = "https://apps.apple.com/account/subscriptions";
export const APPLE_BILLING_NOTE =
  "You subscribe through the App Store on your iPhone."; // Wording pending (proposed)
export const STRIPE_BILLING_NOTE =
  "Billing is handled by Stripe. Your card is only ever used for your plan."; // Wording pending
export const CANCEL_TITLE = "Are you sure?"; // Wording pending
export const CANCEL_BODY = "You are attempting to cancel your subscription."; // Wording pending
export const CANCEL_KEEP = "Keep my plan"; // Wording pending
export const CANCEL_CONFIRM = "Confirm"; // Wording pending
export const CANCELLED_NO_CHANGE =
  "You have canceled your subscription. You can not change your plan!"; // Wording pending (drawn)
// The one copy added after W1 (fix wave item 6): a comped client's plan is
// the server's to change, not theirs.
export const COMPED_NOTE =
  "Your plan is provided by Astra, so it can’t be changed here."; // Wording pending (proposed)

// ---- A new card (/plan/card) --------------------------------------------------------
export const CARD_TITLE = "Add a new card"; // Wording pending (proposed)
export const CARD_SUB =
  "It replaces the card on your plan. Nothing is charged now."; // Wording pending (proposed)
export const SAVE_CARD = "Save card"; // Wording pending (proposed)
export const BACK_TO_PROFILE = "Back to Profile"; // Wording pending (proposed)

// ---- Admin Packages (owner item 5, amended; proposed) ------------------------------
export const ADMIN_TIER = "Tier";
export const ADMIN_TIER_NONE = "No tier";
export const ADMIN_TIERS = {
  basic: "Basic",
  essential: "Essentials",
  professional: "Professional",
  premium: "Premium",
};
export const ADMIN_DESCRIPTION = "Description";
export const ADMIN_APPLE_PRODUCT = "App Store product id";
export const ADMIN_STRIPE_STATUS = "Stripe status";
export const ADMIN_SYNCED = "Synced";
export const ADMIN_NEEDS_ATTENTION = "Needs attention";
export const ADMIN_STATUS_REASONS = {
  price_unreachable: "Stripe can’t find this price.",
  price_inactive: "The Stripe price is archived.",
  not_monthly: "The Stripe price is not monthly.",
  amount_mismatch: "The Stripe price doesn’t match this amount.",
};
export const ADMIN_NOTICES_TITLE = "Needs your attention";
export const ADMIN_NOTICE_OLDER_PRICE = (count, name) =>
  `${count} ${count === 1 ? "client is" : "clients are"} still on an older price for ${name}.`;
export const ADMIN_NOTICE_APPLE_MISSING = (name) =>
  `${name} has no App Store product yet.`;
export const ADMIN_NOTICE_WEBHOOK =
  "Stripe’s webhook is not set up for this environment.";
export const ADMIN_NOTICE_CURRENCY_MISMATCH =
  "A Stripe price’s currency differs from its plan’s.";
export const ADMIN_NOTICE_APPLE_INTRO_UNVERIFIED = (productId) =>
  `The free-month offer for ${productId} must be set up in App Store Connect; the app can’t verify it.`;
export const ADMIN_NOTICE_STRIPE_UNREACHABLE =
  "Stripe could not be reached, so prices are unchecked.";
export const ADMIN_NOTICE_GENERIC =
  "Something needs your attention in Stripe or the App Store."; // Wording pending
export const ADMIN_NOTICE_STRIPE_TAX_UNCHECKED =
  "Stripe tax settings are not checked by the app. Verify them in the Stripe dashboard.";

// ---- The advisor inbox (owner item 13) ----------------------------------------------
export const PILL_PLAN_ENDED = "Plan ended"; // Wording pending (proposed)
export const PLAN_NOT_ACTIVE = "This client’s plan is not active.";

// ---- The meeting add-on page and its landing (W6, proposed) -------------------------
export const PAY_AND_BOOK = "Pay and book"; // Wording pending (proposed)
export const PAYMENT_RECEIVED = "Payment received"; // Wording pending (proposed)
export const PAYMENT_RECEIVED_BODY = "Your meeting is booked."; // Wording pending (proposed)
export const GO_TO_MEETINGS = "Go to my meetings"; // Wording pending (proposed)
